import { useState, memo, useCallback, cloneElement } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Tooltip,
  Slide,
  useTheme,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";

/**
 * WidgetFullscreen Component
 * Wrapper that adds fullscreen capability to any widget
 */
const WidgetFullscreen = memo(function WidgetFullscreen({
  children,
  title = "Widget",
  icon,
  disabled = false,
}) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0";
  const bgPaper = isDark ? "#032d42" : "#ffffff";

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Clone children to pass fullscreen state
  const childWithProps = children
    ? cloneElement(children, { isFullscreen })
    : null;

  return (
    <>
      {/* Normal view with fullscreen button */}
      <Box
        sx={{
          position: "relative",
          height: "100%",
          "&:hover .fullscreen-button": {
            opacity: 1,
          },
        }}
      >
        {children}

        {/* Fullscreen trigger button */}
        {!disabled && (
          <Tooltip title="Expand to fullscreen" arrow>
            <IconButton
              className="fullscreen-button"
              onClick={handleOpen}
              aria-label={`Expand ${title} to fullscreen`}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 32,
                height: 32,
                backgroundColor: isDark
                  ? "rgba(0, 0, 0, 0.6)"
                  : "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(4px)",
                border: `1px solid ${borderColor}`,
                borderRadius: "4px",
                opacity: 0,
                transition: "all 0.2s ease",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(0, 0, 0, 0.8)"
                    : "rgba(255, 255, 255, 1)",
                  transform: "scale(1.1)",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1rem",
                  color: textSecondary,
                },
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Fullscreen dialog */}
      <Dialog
        open={isFullscreen}
        onClose={handleClose}
        fullScreen
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{
          sx: {
            backgroundColor: bgPaper,
            backgroundImage: "none",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${borderColor}`,
            backgroundColor: isDark ? "#021e2d" : "#f8fafc",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {icon && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "4px",
                  background: isDark
                    ? `linear-gradient(135deg, ${greenColor}25 0%, ${greenColor}10 100%)`
                    : `linear-gradient(135deg, ${navyColor}15 0%, ${navyColor}05 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isDark ? greenColor : navyColor,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.25rem",
                  },
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: textPrimary,
              }}
            >
              {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Exit fullscreen (ESC)" arrow>
              <IconButton
                onClick={handleClose}
                aria-label="Exit fullscreen"
                sx={{
                  backgroundColor: isDark
                    ? "rgba(148, 163, 184, 0.1)"
                    : "#f1f5f9",
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(148, 163, 184, 0.2)"
                      : "#e2e8f0",
                  },
                }}
              >
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton
                onClick={handleClose}
                aria-label="Close"
                sx={{
                  backgroundColor: isDark
                    ? "rgba(239, 68, 68, 0.1)"
                    : "rgba(239, 68, 68, 0.08)",
                  color: "#ef4444",
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(239, 68, 68, 0.2)"
                      : "rgba(239, 68, 68, 0.15)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            padding: "24px",
            overflow: "auto",
          }}
        >
          {childWithProps}
        </Box>
      </Dialog>
    </>
  );
});

export default WidgetFullscreen;
